import React, { useState, useEffect, useCallback } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Paper,
  TablePagination,
  IconButton,
  Typography,
  Button,
  TableFooter,
} from "@material-ui/core";
import { useTheme, makeStyles } from "@material-ui/core/styles";
import { CloudDownloadOutlined } from "@material-ui/icons";
import axios from "axios";
import ReportTableShimmer from "./ReportTableShimmer";

type Order = "asc" | "desc";

export interface Data {
  index: number;
  name: string;
  size: string;
  created_at: string;
  content_type: string;
  signed_url: string;
}

interface EnhancedTableProps {
  onRequestSort: (event: React.MouseEvent<unknown>, property: keyof Data) => void;
  order: Order;
  orderBy: string;
  rowCount: number;
}

interface HeadCell {
  disablePadding: boolean;
  id: keyof Data;
  label: string;
  numeric: boolean;
}

function descendingComparator<T>(a: T, b: T, orderBy: keyof T) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

function getComparator<Key extends keyof any>(
  order: Order,
  orderBy: Key,
): (
  a: { [key in Key]: number | string },
  b: { [key in Key]: number | string },
) => number {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function stableSort<T>(array: readonly T[], comparator: (a: T, b: T) => number) {
  const stabilizedThis = array.map((el, index) => [el, index] as [T, number]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) {
      return order;
    }
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
}

const headCells: readonly HeadCell[] = [
  { id: "index", numeric: false, disablePadding: false, label: "Index" },
  { id: "name", numeric: false, disablePadding: false, label: "Report Name" },
  { id: "size", numeric: false, disablePadding: false, label: "File Size" },
  { id: "content_type", numeric: false, disablePadding: false, label: "Content Type" },
  { id: "created_at", numeric: false, disablePadding: false, label: "Created At" },
  { id: "signed_url", numeric: false, disablePadding: false, label: "Download" },
]

const useStyles = makeStyles((theme) => ({
  errorContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: theme.spacing(4),
  },
  errorMessage: {
    marginBottom: theme.spacing(2),
  },
}));


const EnhancedTableHead = ({ onRequestSort, order, orderBy }: EnhancedTableProps) => {
  const createSortHandler = (property: keyof Data) => (event: React.MouseEvent<unknown>) => {
    onRequestSort(event, property);
  };

  return (
    <TableHead>
      <TableRow>
        {headCells.map((headCell) => (
          <TableCell
            key={headCell.id}
            align={headCell.id === 'signed_url' ? 'center' : 'left'}
            padding={headCell.disablePadding ? 'none' : 'default'}
            sortDirection={orderBy === headCell.id ? order : false}
          >
            <TableSortLabel
              active={orderBy === headCell.id}
              direction={orderBy === headCell.id ? order : 'asc'}
              onClick={createSortHandler(headCell.id)}
            >
              {headCell.label}
            </TableSortLabel>
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
};

export const ReportTable = ({ namespace }: { namespace: string }) => {
  const theme = useTheme();
  const classes = useStyles();
  const [order, setOrder] = useState<Order>("asc");
  const [orderBy, setOrderBy] = useState<keyof Data>("index");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [data, setData] = useState<Data[]>([]);
  const [totalItems, setTotalItems] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchReports = useCallback(async () => {
    setIsLoading(true);
    try {
      const res = await axios.get(
        `https://api.onemetrics-report-service.hardware-inno.gcpprodau.tbm.sh/api/v1/report/reports?folder_name=${namespace}&page=${page + 1
        }&page_size=${rowsPerPage}`,
        {
          headers: {
            "Content-Type": "application/json",
          },
        },
      );
      setData(res.data.items);
      setTotalItems(res.data.total_items);
      setError(null);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Fetch report error:", error);
      setError("Failed to fetch reports. Please try again.");
    } finally {
      setIsLoading(false);
    }
  }, [namespace, page, rowsPerPage]);

  useEffect(() => {
    fetchReports();
  }, [fetchReports]);

  const handleRequestSort = (
    event: React.MouseEvent<unknown>,
    property: keyof Data,
  ) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  if (isLoading) { return <ReportTableShimmer />; }

  if (error) {
    return (
      <div className={classes.errorContainer}>
        <Typography variant="h6" className={classes.errorMessage}>
          Something went wrong: {error}
        </Typography>
        <Button variant="contained" color="primary" onClick={fetchReports}>
          Retry
        </Button>
      </div>
    );
  }

  return (
    <>
      <TableContainer component={(props) => <Paper {...props} elevation={0} />}>
        <Table aria-label="report table" size="small">
          <EnhancedTableHead
            order={order}
            orderBy={orderBy}
            onRequestSort={handleRequestSort}
            rowCount={data.length}
          />
          <TableBody>
            {stableSort(data, getComparator(order, orderBy)).map((row) => (
              <TableRow key={row.index}>
                <TableCell
                  className={theme.palette.text.primary}
                  component="th"
                  scope="row"
                >
                  {row.index}
                </TableCell>
                <TableCell className={theme.palette.text.primary} align="left">
                  {row.name}
                </TableCell>
                <TableCell className={theme.palette.text.primary} align="left">
                  {row.size}
                </TableCell>
                <TableCell className={theme.palette.text.primary} align="left">
                  {row.content_type}
                </TableCell>
                <TableCell className={theme.palette.text.primary} align="left">
                  {formatDate(row.created_at)}
                </TableCell>
                <TableCell
                  className={theme.palette.text.primary}
                  align="center"
                >
                  <IconButton
                    onClick={() => window.open(row.signed_url, "_blank")}
                  >
                    <CloudDownloadOutlined />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
          <TableFooter>
            <TableRow>
              <TablePagination
                rowsPerPageOptions={[10, 25, 50]}
                count={totalItems}
                rowsPerPage={rowsPerPage}
                page={page}
                onChangePage={handleChangePage}
                onChangeRowsPerPage={handleChangeRowsPerPage}
              />
            </TableRow>
          </TableFooter>
        </Table>
      </TableContainer>

    </>
  );
};
